import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../api';

interface Stats {
  totalUsers: number;
  totalSessions: number;
  activeSessions: number;
}

interface UserRow {
  email: string;
  name: string;
  role: string;
  createdAt: string;
}

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const [stats, setStats] = useState<Stats | null>(null);
  const [users, setUsers] = useState<UserRow[]>([]);
  const [loadError, setLoadError] = useState('');

  useEffect(() => {
    // These endpoints don't exist yet — handle gracefully.
    api.get('/api/admin/stats')
      .then(r => setStats(r.data))
      .catch(() => {/* backend admin endpoints not yet built */});

    api.get('/api/admin/users')
      .then(r => setUsers(r.data.users || []))
      .catch(() => setLoadError('Admin data endpoints not yet implemented on the backend.'));
  }, []);

  const handleLogout = () => {
    logout();
    navigate('/login', { replace: true });
  };

  const statCard = (label: string, value: number | string, color: string) => (
    <div style={{ flex: 1, minWidth: 120, background: '#f5f7fa', borderRadius: '8px', padding: '18px 14px', textAlign: 'center' }}>
      <div style={{ fontSize: '0.75rem', color: '#777', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '6px' }}>{label}</div>
      <div style={{ fontSize: '2rem', fontWeight: 700, color }}>{value}</div>
    </div>
  );

  return (
    <div style={{ padding: '24px', maxWidth: '900px', margin: '0 auto', fontFamily: 'Arial' }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '28px', flexWrap: 'wrap', gap: '12px' }}>
        <div>
          <h1 style={{ margin: 0, fontSize: '1.6rem' }}>Admin Dashboard</h1>
          <p style={{ margin: '4px 0 0', color: '#666', fontSize: '14px' }}>
            Signed in as <strong>{user?.name}</strong> ({user?.email})
          </p>
        </div>
        <div style={{ display: 'flex', gap: '10px' }}>
          <button
            onClick={() => navigate('/app')}
            style={{ padding: '8px 16px', fontSize: '14px', background: '#2196F3', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}
          >
            Open Roleplay
          </button>
          <button
            onClick={handleLogout}
            style={{ padding: '8px 16px', fontSize: '14px', background: '#f44336', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}
          >
            Log Out
          </button>
        </div>
      </div>

      {/* Stats row */}
      <div style={{ display: 'flex', gap: '14px', marginBottom: '28px', flexWrap: 'wrap' }}>
        {stats ? (
          <>
            {statCard('Total Users', stats.totalUsers, '#1a1a2e')}
            {statCard('Total Sessions', stats.totalSessions, '#1a1a2e')}
            {statCard('Active Sessions', stats.activeSessions, '#2e7d32')}
          </>
        ) : (
          <>
            {statCard('Total Users', '—', '#bbb')}
            {statCard('Total Sessions', '—', '#bbb')}
            {statCard('Active Sessions', '—', '#bbb')}
          </>
        )}
      </div>

      {/* Backend notice */}
      {loadError && (
        <div style={{ background: '#fff8e1', border: '1px solid #ffe082', borderRadius: '6px', padding: '12px 16px', marginBottom: '24px', fontSize: '14px', color: '#795548' }}>
          ⚠️ {loadError}
          <br />
          <span style={{ fontSize: '12px', color: '#999' }}>
            Add <code>GET /api/admin/users</code> and <code>GET /api/admin/stats</code> to the backend to populate this dashboard.
          </span>
        </div>
      )}

      {/* Users table */}
      <div style={{ border: '1px solid #ddd', borderRadius: '8px', overflow: 'hidden' }}>
        <div style={{ background: '#f5f7fa', padding: '14px 18px', borderBottom: '1px solid #ddd', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h2 style={{ margin: 0, fontSize: '1rem' }}>Registered Users</h2>
          <span style={{ fontSize: '13px', color: '#888' }}>{users.length} user{users.length !== 1 ? 's' : ''}</span>
        </div>

        {users.length === 0 ? (
          <div style={{ padding: '40px', textAlign: 'center', color: '#999' }}>
            No user data available — backend admin endpoint needed.
          </div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: '#fafafa', textAlign: 'left', fontSize: '13px', color: '#555' }}>
                <th style={{ padding: '10px 18px', fontWeight: 600 }}>Name</th>
                <th style={{ padding: '10px 18px', fontWeight: 600 }}>Email</th>
                <th style={{ padding: '10px 18px', fontWeight: 600 }}>Role</th>
                <th style={{ padding: '10px 18px', fontWeight: 600 }}>Joined</th>
              </tr>
            </thead>
            <tbody>
              {users.map((u, i) => (
                <tr key={u.email} style={{ borderTop: '1px solid #eee', background: i % 2 === 0 ? 'white' : '#fafafa' }}>
                  <td style={{ padding: '12px 18px' }}>{u.name}</td>
                  <td style={{ padding: '12px 18px', color: '#555' }}>{u.email}</td>
                  <td style={{ padding: '12px 18px' }}>
                    <span style={{
                      display: 'inline-block',
                      padding: '2px 10px',
                      borderRadius: '12px',
                      fontSize: '12px',
                      fontWeight: 600,
                      background: u.role === 'admin' ? '#e8f5e9' : '#e3f2fd',
                      color: u.role === 'admin' ? '#2e7d32' : '#1565c0',
                    }}>
                      {u.role}
                    </span>
                  </td>
                  <td style={{ padding: '12px 18px', color: '#888', fontSize: '13px' }}>
                    {u.createdAt ? new Date(u.createdAt).toLocaleDateString() : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
